import { useState, useRef } from 'react';
import { Avatar } from '@printtat/ui';
import { Camera, Trash2 } from 'lucide-react';
import { Alert, Button } from '@printtat/ui';
import type { User } from '@printtat/shared';
import { apiFetch } from '@/lib/apiFetch';

interface ProfilePictureUploadProps {
  user: User;
  currentPicture?: string;
  onUploadSuccess?: (pictureUrl: string) => void;
  onDeleteSuccess?: () => void;
  compact?: boolean;
}

export const ProfilePictureUpload = ({
  user,
  currentPicture,
  onUploadSuccess,
  onDeleteSuccess,
  compact = false,
}: ProfilePictureUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getInitials = () => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";

    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }

    return user.email.charAt(0).toUpperCase();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiFetch('/users/profile-picture', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload profile picture');
      }

      const data = await response.json();
      onUploadSuccess?.(data.profilePicture);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload profile picture');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await apiFetch('/users/profile-picture', {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete profile picture');
      }

      onDeleteSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete profile picture');
    } finally {
      setIsDeleting(false);
    }
  };

  const hasPicture = !!currentPicture;
  const pictureUrl = hasPicture
    ? currentPicture.startsWith('http')
      ? currentPicture
      : `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}${currentPicture}`
    : undefined;

  const avatarSize = compact ? "6" : "9";

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative group">
        <Avatar
          size={avatarSize}
          src={pictureUrl}
          fallback={getInitials()}
          radius="full"
          className="border-2 border-gray-200"
        />
        <button
          type="button"
          onClick={handleFileSelect}
          disabled={isUploading || isDeleting}
          className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 group-hover:bg-black/40 transition-colors cursor-pointer"
        >
          <Camera className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {!compact && (
        <>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleFileSelect}
              disabled={isUploading || isDeleting}
              size="sm"
              loading={isUploading}
            >
              <Camera className="w-3.5 h-3.5" />
              {hasPicture ? 'Change' : 'Upload'}
            </Button>

            {hasPicture && (
              <Button
                variant="outline"
                color="red"
                onClick={handleDelete}
                disabled={isUploading || isDeleting}
                size="sm"
                loading={isDeleting}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove
              </Button>
            )}
          </div>

          <p className="text-xs text-gray-500 text-center">
            JPG, PNG or WebP. Max size 5MB.
          </p>
        </>
      )}

      {compact && hasPicture && (
        <Button
          type="button"
          variant="ghost"
          color="red"
          size="sm"
          onClick={handleDelete}
          disabled={isUploading || isDeleting}
        >
          Remove
        </Button>
      )}

      {error && (
        <Alert variant="error" className="w-full">
          {error}
        </Alert>
      )}
    </div>
  );
};
